import { useState } from "react";
import { Link } from "react-router-dom";
import Desktop from "./Desktop";
import FooterWrapper from "./FooterWrapper";
import FooterLinks from "./FooterLinks";
import { authService } from "../config/api";

const ForgotPasswordForm = () => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            setError("Please enter your email address");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await authService.forgotPassword({ email });
            setSuccess(true);
        } catch (err) {
            setError(err.message || "Failed to send reset email");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-start justify-start min-h-screen">
                <Desktop />

                <div className="w-full flex items-center justify-center py-12 px-4">
                    <div className="max-w-md w-full space-y-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success/20 mb-4">
                                <svg className="h-8 w-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-bold font-heading text-base-content">
                                Check Your Email
                            </h1>
                            <p className="mt-4 text-base text-base-content/70">
                                If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
                            </p>
                            <p className="mt-2 text-sm text-base-content/60">
                                The link will expire in 30 minutes.
                            </p>
                        </div>

                        <div className="mt-8">
                            <Link to="/signin" className="btn btn-primary w-full">
                                Return to Sign In
                            </Link>
                        </div>

                        <div className="text-center text-sm text-base-content/60">
                            Didn't receive an email? Check your spam folder or{" "}
                            <button
                                onClick={() => {
                                    setSuccess(false);
                                    setEmail("");
                                }}
                                className="link link-primary"
                            >
                                try again
                            </button>
                        </div>
                    </div>
                </div>

                <section className="self-stretch flex flex-row items-start justify-start pt-0 px-0 pb-[22px] box-border max-w-full">
                    <div className="flex-1 bg-base-200 flex flex-row items-start justify-start pt-[72px] px-[169px] pb-[71.8px] box-border gap-12 max-w-full lg:flex-wrap mq450:py-[47px] mq450:px-5 mq450:box-border mq750:gap-6 mq750:pl-[84px] mq750:pr-[84px] mq750:box-border">
                        <FooterWrapper />
                        <FooterLinks />
                    </div>
                </section>
                <div className="self-stretch flex flex-row items-start justify-start py-0 pl-[382px] pr-[381px] box-border max-w-full text-base-content/70 mq450:pl-5 mq450:pr-5 mq450:box-border mq750:pl-[191px] mq750:pr-[190px] mq750:box-border">
                    <div className="flex-1 relative leading-[20px] inline-block max-w-full">
                        Copyright @StockAlgoTrader 2024. All Rights Reserved.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-start justify-start min-h-screen">
            <Desktop />

            <div className="w-full flex items-center justify-center py-12 px-4">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold font-heading text-base-content">
                            Forgot Password?
                        </h1>
                        <p className="mt-2 text-base text-base-content/70">
                            No worries! Enter your email and we'll send you reset instructions.
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        {error && (
                            <div className="alert alert-error">
                                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="fieldset w-full">
                            <label className="label">
                                <span className="text-sm">Email Address</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email"
                                className="input input-bordered bg-base-200 w-full"
                                value={email}
                                onChange={(e) => {
                                    setError("");
                                    setEmail(e.target.value);
                                }}
                                disabled={loading}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                "Send Reset Link"
                            )}
                        </button>

                        <div className="text-center">
                            <Link to="/signin" className="link link-primary text-sm">
                                ← Back to Sign In
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            <section className="self-stretch flex flex-row items-start justify-start pt-0 px-0 pb-[22px] box-border max-w-full">
                <div className="flex-1 bg-base-200 flex flex-row items-start justify-start pt-[72px] px-[169px] pb-[71.8px] box-border gap-12 max-w-full lg:flex-wrap mq450:py-[47px] mq450:px-5 mq450:box-border mq750:gap-6 mq750:pl-[84px] mq750:pr-[84px] mq750:box-border">
                    <FooterWrapper />
                    <FooterLinks />
                </div>
            </section>
            <div className="self-stretch flex flex-row items-start justify-start py-0 pl-[382px] pr-[381px] box-border max-w-full text-base-content/70 mq450:pl-5 mq450:pr-5 mq450:box-border mq750:pl-[191px] mq750:pr-[190px] mq750:box-border">
                <div className="flex-1 relative leading-[20px] inline-block max-w-full">
                    Copyright @StockAlgoTrader 2024. All Rights Reserved.
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordForm;
